import { useMemo } from 'react';
import { useNavigation } from '@/contexts/NavigationContext';
import { resolveRoute, resolveNodeRoute } from '@/hooks/useNavigationApi';
import type { NavigationNode } from '@/hooks/useNavigationApi';

/**
 * Mock route-to-name map, kept in sync with useMainNavigation mock data.
 * Used as fallback when navigation API data isn't available.
 */
const mockRouteNames: Record<string, string> = {
  '/units': 'VIN/Unit Search',
  '/inventory-transfers': 'Inventory Transfers',
  '/warranty-registration': 'Unit/Warranty Registration',
  '/my-spiffs': "My Claimed SPIFF's",
  '/claimed-dealer-spiffs': "Dealer Claimed SPIFF's",
  '/salesperson-spiffs': "Sales Person SPIFF's",
  '/dealer-spiffs': "Dealer SPIFF's",
  '/dealer-invoices': 'Dealer Invoices',
  '/service-campaigns': 'Service Campaigns',
  '/rga-list': "RGA's",
  '/dealer-claims': 'Claims and Pre-Auths',
  '/your-tickets': 'Your Tickets',
  '/labor-operations': 'Labor Operations Library',
  '/quick-order': 'Quick Order',
  '/unknown-part-request': 'Unknown Part Request',
  '/message-board': 'Message Board',
  '/digital-assets': 'Digital Assets',
  '/parts': 'Shop RV Parts',
  '/saved-carts': 'Saved Carts',
  '/my-favorites': 'My Favorites',
  '/contact-info': 'My Contact Info',
  
  '/orders': 'Dealer Orders',
  '/addresses': 'Dealer Addresses',
};

/** Recursively walk navigation nodes and build a route->name map */
function buildRouteMap(nodes: NavigationNode[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const node of nodes) {
    const route = resolveNodeRoute(node);
    if (route && route !== '#') {
      map[route] = node.name;
    }
    if (node.nodes) {
      Object.assign(map, buildRouteMap(node.nodes));
    }
  }
  return map;
}

/**
 * Returns a dynamic page title resolved from navigation API data.
 * Falls back to mock names, then to the provided fallback string.
 *
 * @param route  The local route path (e.g. "/units")
 * @param fallback  Default title if navigation data isn't available
 */
export function usePageTitle(route: string, fallback: string): string {
  const { mainMenuNodes, quickLinksNodes, serviceMenuNodes, useMock } = useNavigation();

  return useMemo(() => {
    // In mock mode or when API data hasn't loaded, use mock names
    if (useMock || (!mainMenuNodes && !quickLinksNodes && !serviceMenuNodes)) {
      return mockRouteNames[route] ?? fallback;
    }

    // Build a combined route->name map from all navigation trees
    const routeMap: Record<string, string> = {};
    if (quickLinksNodes) Object.assign(routeMap, buildRouteMap(quickLinksNodes));
    if (mainMenuNodes) Object.assign(routeMap, buildRouteMap(mainMenuNodes));
    if (serviceMenuNodes) Object.assign(routeMap, buildRouteMap(serviceMenuNodes));

    return routeMap[route] ?? mockRouteNames[route] ?? fallback;
  }, [route, fallback, mainMenuNodes, quickLinksNodes, serviceMenuNodes, useMock]);
}

/**
 * Returns the full route->name lookup map for use in Breadcrumbs / NavigationHistory.
 */
export function useRouteNameMap(): Record<string, string> {
  const { mainMenuNodes, quickLinksNodes, serviceMenuNodes, useMock } = useNavigation();

  return useMemo(() => {
    if (useMock || (!mainMenuNodes && !quickLinksNodes && !serviceMenuNodes)) {
      return { ...mockRouteNames };
    }

    const routeMap: Record<string, string> = { ...mockRouteNames };
    if (quickLinksNodes) Object.assign(routeMap, buildRouteMap(quickLinksNodes));
    if (mainMenuNodes) Object.assign(routeMap, buildRouteMap(mainMenuNodes));
    if (serviceMenuNodes) Object.assign(routeMap, buildRouteMap(serviceMenuNodes));

    return routeMap;
  }, [mainMenuNodes, quickLinksNodes, serviceMenuNodes, useMock]);
}

// ─── Route-to-parent mapping ───

const mockRouteParents: Record<string, string> = {
  '/units': 'Units',
  '/inventory-transfers': 'Units',
  '/parts': 'Parts',
  '/quick-order': 'Parts',
  '/unknown-part-request': 'Parts',
  '/service-campaigns': 'Service',
  '/rga-list': 'Service',
  '/dealer-claims': 'Service',
  '/your-tickets': 'Service',
  '/labor-operations': 'Service',
  '/warranty-registration': 'Sales',
  '/my-spiffs': 'Sales',
  '/claimed-dealer-spiffs': 'Sales',
  '/salesperson-spiffs': 'Sales',
  '/dealer-spiffs': 'Sales',
  '/dealer-invoices': 'Sales',
  '/saved-carts': 'My Account',
  '/my-favorites': 'My Account',
  '/contact-info': 'My Account',
  
  '/orders': 'My Dealership',
  '/addresses': 'My Dealership',
  '/dealer-users': 'My Dealership',
  '/dealership-info': 'My Dealership',
};

/** Build a route-to-parent-name map from navigation tree nodes */
function buildRouteParentMap(nodes: NavigationNode[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const node of nodes) {
    if (node.nodes && node.nodes.length > 0) {
      for (const child of node.nodes) {
        const route = resolveNodeRoute(child);
        if (route && route !== '#') {
          map[route] = node.name;
        }
      }
      // Recurse for deeper nesting
      Object.assign(map, buildRouteParentMap(node.nodes));
    }
  }
  return map;
}

/**
 * Returns a route-to-parent-group-name map derived from navigation trees.
 * E.g. { '/units': 'Units', '/contact-info': 'My Account' }
 */
export function useRouteParentMap(): Record<string, string> {
  const { mainMenuNodes, serviceMenuNodes, useMock } = useNavigation();

  return useMemo(() => {
    if (useMock || (!mainMenuNodes && !serviceMenuNodes)) {
      return { ...mockRouteParents };
    }

    const parentMap: Record<string, string> = { ...mockRouteParents };
    if (mainMenuNodes) Object.assign(parentMap, buildRouteParentMap(mainMenuNodes));
    if (serviceMenuNodes) Object.assign(parentMap, buildRouteParentMap(serviceMenuNodes));

    return parentMap;
  }, [mainMenuNodes, serviceMenuNodes, useMock]);
}
